import {
  AxiosResponse,
  AxiosError,
  CancelTokenSource,
  default as axios,
  AxiosPromise,
  Method,
} from 'axios';
import { store } from 'src/Store';
import ErrorResponse from 'src/Models/ErrorResponse';
import { CustomHeaders } from '../../Models/CustomHeader';
import { Settings, UATConstants } from '../Models/UATConstants';
import { IEventTelemetry, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { getWindowLocationHref } from './BrowserGlobals';
import { appInsights } from '../../Services/Insights';
import AuthProvider from 'src/AuthProvider';
import { onTokenRenewal } from 'src/Token';

export interface BasicClientInterface {
  doRequest_New: (
    method: Method,
    urlOrApi: string,
    // tslint:disable-next-line:no-any
    data: any,
    beforeRequest: () => void,
    onRequestSuccess: (response: AxiosResponse) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    statusMessageId?: string,
    onRequestCancelled?: () => void,
    customRequestTimeOut?: number,
    supressClientErrorPopup?: boolean
  ) => void;
}

class UATWebApi implements BasicClientInterface {
  apiErrorMessage: string;
  serviceUnreachableMessage: string;

  constructor() {
    // TODO: Move to constants
    this.apiErrorMessage =
      'An error has occurred. Please try again and leave feedback (bottom right corner of the page) for our awareness';
    this.serviceUnreachableMessage = 'Service unreachable';
  }

  getWebApiEndpointUrl(): string {
    return store.getState().environmentSettings.webApiEndpointUrl;
  }

  getAxiosRequestTimeOut(): number {
    return 180000;
  }

  doCancelRequest(source: CancelTokenSource, message?: string): void {
    if (source) {
      source.cancel(message);
    }
  }

  // Takes a response object and parses refreshed lite Jwt token out of headers. If found then token cache gets updated
  jwtTokenWasUpdated(response?: AxiosResponse): boolean {
    try {
      if (response && response.headers) {
        // tslint:disable-next-line:no-string-literal
        const refreshHeaderValue = response.headers['x-uat-jwtliterefresh'];
        if (refreshHeaderValue) {
          store.getState().authentication.context.setAuthenticationProperties(refreshHeaderValue);
          return true;
        }
      }
    } catch (errorRefresh) {
      // Ignore. No headers and/or no refresh token.
    }
    return false;
  }
  // Takes a response object and parses stpstaletoken header. Returns true in case a token is stale.
  checkIfTokenIsStale(response?: AxiosResponse): boolean {
    try {
      if (response && response.headers) {
        const refreshHeaderValue = response.headers.stpstaletoken;
        if (refreshHeaderValue) {
          return refreshHeaderValue === 'jwtlite' || refreshHeaderValue === 'accesstoken';
        }
      }
    } catch (errorRefresh) {
      // Ignore. No headers and/or no refresh token.
    }
    return false;
  }

  checkIfUserSessionIsAlive(response?: AxiosResponse): boolean {
    // If we received new jwt lite the continue normally
    if (!this.jwtTokenWasUpdated(response)) {
      // Check if jwt token is stale.
      if (this.checkIfTokenIsStale(response)) {
        // Jwt token is stale. We must force the logout
        const context = store.getState().authentication.context;
        if (context && context.user && context.user.authData) {
          context.logOut();
          return false;
        }
      }
    }
    return true;
  }

  tryRenewAllTokens(): any {
    if (UATConstants.IsNotDev()) {
      return Promise.all([
        this.tryRenewUATAPIToken(),
        this.tryRenewGraphToken(),
        this.tryRenewCoreAPIToken(),
      ]).then(function (values) {
        return values;
      });
    }
    return Promise.all([
      this.tryRenewUATAPIToken(),
      this.tryRenewGraphToken(),
      this.tryRenewCoreAPIToken(),
    ]).then(function (values) {
      return values;
    });
  }

  tryRenewTokens(): any {
    if (UATConstants.IsNotDev()) {
      return Promise.all([
        this.tryRenewUATAPIToken(),
        this.tryRenewCoreAPIToken(),
        this.tryRenewDevopsToken(),
      ]).then(function (values) {
        return values;
      });
    }
    return Promise.all([
      this.tryRenewUATAPIToken(),
      this.tryRenewCoreAPIToken(),
      this.tryRenewDevopsToken(),
    ]).then(function (values) {
      return values;
    });
  }

  async tryRenewCoreAPIToken() {
    let coreApiToken = sessionStorage.getItem(UATConstants.jwtTokenForCoreApi);
    const coreExpiryTime =
      Date.parse(sessionStorage.getItem(UATConstants.coreTokenExpiryTime) || '') - Date.now();
    if (
      coreApiToken == null ||
      coreApiToken == undefined ||
      isNaN(coreExpiryTime) ||
      (coreExpiryTime > 0 && coreExpiryTime < Settings.PREFETCH_TOKEN_TIME) ||
      coreExpiryTime <= 0
    ) {
      await onTokenRenewal(UATConstants.PlaceholdersAndTitles.Core);
      coreApiToken = sessionStorage.getItem(UATConstants.jwtTokenForCoreApi);
    }

    return coreApiToken;
  }

  async tryRenewDevopsToken() {
    let devopsToken = sessionStorage.getItem(UATConstants.jwtTokenForADOApi);
    const devopsExpiryTime =
      Date.parse(sessionStorage.getItem(UATConstants.devopsTokenExpiryTime) || '') - Date.now();
    if (
      devopsToken == null ||
      devopsToken == undefined ||
      isNaN(devopsExpiryTime) ||
      (devopsExpiryTime > 0 && devopsExpiryTime < Settings.PREFETCH_TOKEN_TIME) ||
      devopsExpiryTime <= 0
    ) {
      await onTokenRenewal(UATConstants.PlaceholdersAndTitles.Devops);
      devopsToken = sessionStorage.getItem(UATConstants.jwtTokenForADOApi);
    }

    return devopsToken;
  }

  async tryRenewUATAPIToken() {
    let apiToken = sessionStorage.getItem(UATConstants.jwtTokenForApi);
    const expiryTime =
      Date.parse(sessionStorage.getItem(UATConstants.loginTokenExpiryTime) || '') - Date.now();
    if (
      apiToken == null ||
      apiToken == undefined ||
      isNaN(expiryTime) ||
      (expiryTime > 0 && expiryTime < Settings.PREFETCH_TOKEN_TIME) ||
      expiryTime <= 0
    ) {
      await onTokenRenewal(UATConstants.PlaceholdersAndTitles.Api);
      apiToken = sessionStorage.getItem(UATConstants.jwtTokenForApi);
    }

    return apiToken;
  }

  async tryRenewUATADOToken() {
    let apiToken = sessionStorage.getItem(UATConstants.jwtTokenForADOApi);
    const expiryTime =
      Date.parse(sessionStorage.getItem(UATConstants.devopsTokenExpiryTime) || '') - Date.now();
    if (
      apiToken == null ||
      apiToken == undefined ||
      isNaN(expiryTime) ||
      (expiryTime > 0 && expiryTime < Settings.PREFETCH_TOKEN_TIME) ||
      expiryTime <= 0
    ) {
      await onTokenRenewal(UATConstants.PlaceholdersAndTitles.Devops);
      apiToken = sessionStorage.getItem(UATConstants.jwtTokenForADOApi);
    }

    return apiToken;
  }

  async tryRenewGraphToken() {
    let graphToken = sessionStorage.getItem(UATConstants.jwtTokenForGraph);
    const graphTokenExpiryTime =
      Date.parse(sessionStorage.getItem(UATConstants.graphTokenExpiryTime) || '') - Date.now();
    if (
      graphToken == null ||
      graphToken == undefined ||
      isNaN(graphTokenExpiryTime) ||
      (graphTokenExpiryTime > 0 && graphTokenExpiryTime < Settings.PREFETCH_TOKEN_TIME) ||
      graphTokenExpiryTime <= 0
    ) {
      await onTokenRenewal(UATConstants.PlaceholdersAndTitles.Graph);
      graphToken = sessionStorage.getItem(UATConstants.jwtTokenForGraph);
    }

    return graphToken;
  }

  doRequest_New_NoCache(
    method: Method = 'post',
    urlOrApi: string,
    // tslint:disable-next-line:no-any
    data: any,
    beforeRequest: () => void,
    onRequestSuccess: (response: AxiosResponse) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    statusMessageId: string = '_common',
    onRequestCancelled?: () => void,
    customRequestTimeOut?: number,
    isCoreAPI?: boolean,
    supressClientErrorPopup?: boolean
  ): CancelTokenSource {
    const webAPIUrl = UATConstants.WebApiUrl;

    const source: CancelTokenSource = axios.CancelToken.source();
    if (beforeRequest != null) {
      beforeRequest();
    }
    const urlToUse: string = urlOrApi.startsWith('/api/', 0)
      ? webAPIUrl.concat(urlOrApi)
      : urlOrApi;

    this.tryRenewAllTokens().then((values: string[]) => {
      const [apiToken, graphToken, coreApiToken] = values;
      // Only add the Authorization header if there is actually an authorization token.
      const headersToUse = new CustomHeaders();

      // Add authorization header that contains jwt lite
      if (apiToken) {
        headersToUse.Authorization = `Bearer ${apiToken}`;
        headersToUse.GraphToken = `${graphToken}`;
        headersToUse.CoreApiToken = `${coreApiToken}`;
        headersToUse['Cache-Control'] = 'no-cache';
        headersToUse.Pragma = 'no-cache';
        headersToUse.Expires = '0';
      }
      const startTime = Date.now();
      axios({
        method: method,
        url: urlToUse,
        headers: { ...headersToUse },
        data: data,
        cancelToken: source.token,
        timeout: customRequestTimeOut ? customRequestTimeOut : this.getAxiosRequestTimeOut(),
      })
        .then((response: AxiosResponse) => {
          try {
            onRequestSuccess(response);
            const endTime = Date.now();
            const ajaxRequest: IEventTelemetry = {
              name: 'UATWebApiRequest',
            };
            const properties = {
              WebApiUrl: urlToUse,
              WebApiMethod: method,
              WebUIUrl: getWindowLocationHref(),
              totalMilliseconds: endTime - startTime,
              requestedUserName: sessionStorage.getItem('jwtUserAccountName'),
              requestedUserAlias: sessionStorage.getItem('jwtUserAccountEmail'),
            };
            appInsights.trackEvent(ajaxRequest, properties);
          } catch (error) {
            console.log(error);
          }
        })
        .catch((error: AxiosError) => {
          onRequestFailed({
            code: error.response ? error.response.status : error.code == 'ECONNABORTED' ? -1 : 0,
            detailedErrorMessages:
              error.response && error.response.data ? error.response.data : undefined,
            message: error.response && error.response.data ? error.response.data.message : '',
            request: error.request,
            response: error.response,
            status: error.response ? error.response.statusText : '',
          });
          const exception: IExceptionTelemetry = {
            exception: error,
            properties: {
              name: 'UATWebApiError',
            },
          };
          appInsights.trackException(exception);
        });
    });
    return source;
  }

  doRequest_New(
    method: Method = 'post',
    urlOrApi: string,
    // tslint:disable-next-line:no-any
    data: any,
    beforeRequest: () => void,
    onRequestSuccess: (response: AxiosResponse) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    statusMessageId: string = '_common',
    onRequestCancelled?: () => void,
    customRequestTimeOut?: number,
    isCoreAPI?: boolean,
    supressClientErrorPopup?: boolean
  ): CancelTokenSource {
    const webAPIUrl = UATConstants.WebApiUrl;

    const source: CancelTokenSource = axios.CancelToken.source();
    if (beforeRequest != null) {
      beforeRequest();
    }
    const urlToUse: string = urlOrApi.startsWith('/api/', 0)
      ? webAPIUrl.concat(urlOrApi)
      : urlOrApi;

    this.tryRenewAllTokens().then((values: string[]) => {
      const [apiToken, graphToken, coreApiToken] = values;
      // Only add the Authorization header if there is actually an authorization token.
      const headersToUse = new CustomHeaders();
      // Add authorization header that contains jwt lite
      if (apiToken) {
        headersToUse.Authorization = `Bearer ${apiToken}`;
        headersToUse.GraphToken = `${graphToken}`;
        headersToUse.CoreApiToken = `${coreApiToken}`;
      }
      const startTime = Date.now();
      axios({
        method: method,
        url: urlToUse,
        headers: { ...headersToUse },
        data: data,
        cancelToken: source.token,
        timeout: customRequestTimeOut ? customRequestTimeOut : this.getAxiosRequestTimeOut(),
      })
        .then((response: AxiosResponse) => {
          try {
            onRequestSuccess(response);
            const endTime = Date.now();
            const ajaxRequest: IEventTelemetry = {
              name: 'UATWebApiRequest',
            };
            const properties = {
              WebApiUrl: urlToUse,
              WebApiMethod: method,
              WebUIUrl: getWindowLocationHref(),
              totalMilliseconds: endTime - startTime,
              requestedUserName: sessionStorage.getItem('jwtUserAccountName'),
              requestedUserAlias: sessionStorage.getItem('jwtUserAccountEmail'),
            };
            appInsights.trackEvent(ajaxRequest, properties);
          } catch (error) {
            console.log(error);
          }
        })
        .catch((error: AxiosError) => {
          onRequestFailed({
            code: error.response ? error.response.status : error.code == 'ECONNABORTED' ? -1 : 0,
            detailedErrorMessages:
              error.response && error.response.data ? error.response.data : undefined,
            message: error.response && error.response.data ? error.response.data.message : '',
            request: error.request,
            response: error.response,
            status: error.response ? error.response.statusText : '',
          });
          const exception: IExceptionTelemetry = {
            exception: error,
            properties: {
              name: 'UATWebApiError',
            },
          };
          appInsights.trackException(exception);
        });
    });
    return source;
  }

  doAdoRequest_New(
    method: Method = 'post',
    urlOrApi: string,
    // tslint:disable-next-line:no-any
    data: any,
    beforeRequest: () => void,
    onRequestSuccess: (response: AxiosResponse) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    statusMessageId: string = '_common',
    onRequestCancelled?: () => void,
    customRequestTimeOut?: number,
    isVSTSAPI?: boolean,
    supressClientErrorPopup?: boolean
  ): CancelTokenSource {
    const webAPIUrl = UATConstants.WebApiUrl;

    const source: CancelTokenSource = axios.CancelToken.source();
    if (beforeRequest != null) {
      beforeRequest();
    }
    const urlToUse: string = urlOrApi.startsWith('/api/', 0)
      ? webAPIUrl.concat(urlOrApi)
      : urlOrApi;

    this.tryRenewTokens().then((values: string[]) => {
      const [apiToken, coreApiToken, adoToken] = values;

      // Only add the Authorization header if there is actually an authorization token.
      const headersToUse = new CustomHeaders();
      // Add authorization header that contains jwt lite
      if (apiToken) {
        headersToUse.Authorization = `Bearer ${apiToken}`;
        headersToUse.CoreApiToken = `${coreApiToken}`;
        headersToUse.AdoToken = `${adoToken}`;
      }
      const startTime = Date.now();
      axios({
        method: method,
        url: urlToUse,
        headers: { ...headersToUse },
        data: data,
        cancelToken: source.token,
        timeout: customRequestTimeOut ? customRequestTimeOut : this.getAxiosRequestTimeOut(),
      })
        .then((response: AxiosResponse) => {
          try {
            onRequestSuccess(response);
            const endTime = Date.now();
            const ajaxRequest: IEventTelemetry = {
              name: 'UATWebApiRequest',
            };
            const properties = {
              WebApiUrl: urlToUse,
              WebApiMethod: method,
              WebUIUrl: getWindowLocationHref(),
              totalMilliseconds: endTime - startTime,
              requestedUserName: sessionStorage.getItem('jwtUserAccountName'),
              requestedUserAlias: sessionStorage.getItem('jwtUserAccountEmail'),
            };
            appInsights.trackEvent(ajaxRequest, properties);
          } catch (error) {
            console.log(error);
          }
        })
        .catch((error: AxiosError) => {
          onRequestFailed({
            code: error.response ? error.response.status : 0,
            detailedErrorMessages:
              error.response && error.response.data ? error.response.data : undefined,
            message: error.response && error.response.data ? error.response.data.message : '',
            request: error.request,
            response: error.response,
            status: error.response ? error.response.statusText : '',
          });
          const exception: IExceptionTelemetry = {
            exception: error,
            properties: {
              name: 'UATWebApiError',
            },
          };
          appInsights.trackException(exception);
        });
    });
    return source;
  }

  async doRequest_Async<T>(
    method: Method = 'post',
    urlOrApi: string,
    data: any,
    beforeRequest?: () => void,
    customRequestTimeOut?: number
  ): Promise<T> {
    const webAPIUrl = UATConstants.WebApiUrl;
    if (beforeRequest != null) {
      beforeRequest();
    }
    const urlToUse: string = urlOrApi.startsWith('/api/', 0)
      ? webAPIUrl.concat(urlOrApi)
      : urlOrApi;
    let [apiToken, graphToken, coreApiToken] = Array(3).fill('');

    if (UATConstants.IsNotDev()) {
      [apiToken, graphToken, coreApiToken] = await Promise.all([
        this.tryRenewUATAPIToken(),
        this.tryRenewGraphToken(),
        this.tryRenewCoreAPIToken(),
      ]);
    } else {
      [apiToken, graphToken, coreApiToken] = await Promise.all([
        this.tryRenewUATAPIToken(),
        this.tryRenewGraphToken(),
        this.tryRenewCoreAPIToken(),
      ]);
    }

    // Only add the Authorization header if there is actually an authorization token.
    const headersToUse = new CustomHeaders();
    // Add authorization header that contains jwt lite
    if (apiToken) {
      headersToUse.Authorization = `Bearer ${apiToken}`;
      headersToUse.GraphToken = `${graphToken}`;
      headersToUse.CoreApiToken = `${coreApiToken}`;
    }

    const contentTypeHeader = {
      'Content-Type': 'application/json',
    };

    const axiosHeaders: any = { ...contentTypeHeader, ...headersToUse };

    const startTime = Date.now();
    const response = await axios({
      method: method,
      url: urlToUse,
      headers: axiosHeaders,
      data: data,
      timeout: customRequestTimeOut ? customRequestTimeOut : this.getAxiosRequestTimeOut(),
    }).then((response) => {
      const endTime = Date.now();
      const responseData: T = response.data;
      const ajaxRequest: IEventTelemetry = {
        name: 'UATWebApiRequest',
      };
      const properties = {
        WebApiUrl: urlToUse,
        WebApiMethod: method,
        WebUIUrl: getWindowLocationHref(),
        totalMilliseconds: endTime - startTime,
        StartTime: startTime,
        EndTime: endTime,
        requestedUserName: sessionStorage.getItem('jwtUserAccountName'),
        requestedUserAlias: sessionStorage.getItem('jwtUserAccountEmail'),
      };
      appInsights.trackEvent(ajaxRequest, properties);
      return responseData;
    });

    return response;
  }

  downloadFileWithFilter(
    api: string,
    fileName: string,
    // tslint:disable-next-line:no-any
    data: any
  ) {
    const url = this.getWebApiEndpointUrl().concat(api);
    axios
      .request({
        responseType: 'arraybuffer',
        url: url,
        method: 'post',
        headers: {
          Authorization: store.getState().authentication.idToken,
        },
        data: data,
      })
      .then((result: AxiosResponse) => {
        this.saveFile(result, fileName);
        return;
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          if (error.response.data.HttpStatusCode === 404 || error.response.status === 404) {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(Intl.Get(LocIds.ServiceTrustClient.ResourceNotFound), 'error_ComplianceManager')
            // );
          } else {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(this.apiErrorMessage, '_common')
            // );
          }
        }
      });
  }

  downloadFileWithFilterV2(api: string, fileName: string, data: {}) {
    const url = this.getWebApiEndpointUrl().concat(api);
    axios
      .request({
        responseType: 'arraybuffer',
        url: url,
        method: 'post',
        headers: {
          Authorization: store.getState().authentication.idToken,
        },
        data: data,
      })
      .then((result: AxiosResponse) => {
        this.saveFile(result, fileName);
        return;
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          if (error.response.data.HttpStatusCode === 404 || error.response.status === 404) {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(Intl.Get(LocIds.ServiceTrustClient.ResourceNotFound), 'error_ComplianceManager')
            // );
          } else {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(this.apiErrorMessage, '_common')
            // );
          }
        }
      });
  }

  downloadFileWithFilterV3(api: string, fileName: string, tileId: string): AxiosPromise {
    const url = this.getWebApiEndpointUrl().concat(api + '/' + tileId);
    return axios.request({
      responseType: 'arraybuffer',
      url: url,
      method: 'post',
      headers: {
        Authorization: store.getState().authentication.idToken,
      },
    });
  }

  downloadUserGDPRPrivacyData(api: string, fileName: string, data: {}, callback: () => void) {
    const url = this.getWebApiEndpointUrl().concat(api);
    axios
      .request({
        responseType: 'arraybuffer',
        url: url,
        method: 'post',
        headers: {
          Authorization: store.getState().authentication.idToken,
        },
        data: data,
      })
      .then((result: AxiosResponse) => {
        if (result.status === 204) {
          alert('No assigned control action items or uploaded documents for this user.');
          callback();
          return;
        }
        this.saveFile(result, fileName);
        callback();
        return;
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          if (error.response.data.HttpStatusCode === 404 || error.response.status === 404) {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(Intl.Get(LocIds.ServiceTrustClient.ResourceNotFound), 'error_ComplianceManager')
            // );
          } else {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(this.apiErrorMessage, '_common')
            // );
          }
        }
        callback();
      });
  }

  downloadFile(api: string, fileName: string) {
    const url = this.getWebApiEndpointUrl().concat(api);
    axios
      .request({
        responseType: 'arraybuffer',
        url: url,
        method: 'post',
        headers: {
          Authorization: store.getState().authentication.idToken,
        },
      })
      .then((result: AxiosResponse) => {
        this.saveFile(result, fileName);
        return;
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          if (error.response.data.HttpStatusCode === 404 || error.response.status === 404) {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(Intl.Get(LocIds.ServiceTrustClient.ResourceNotFound), 'error_ComplianceManager')
            // );
          } else {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(this.apiErrorMessage, '_common')
            // );
          }
        }
      });
  }

  downloadEvidence(api: string, fileName: string, controlFamilyId: string, controlId: string) {
    const url = this.getWebApiEndpointUrl().concat(api + '/' + controlFamilyId + '/' + controlId);
    axios
      .request({
        responseType: 'arraybuffer',
        url: url,
        method: 'get',
        headers: {
          Authorization: store.getState().authentication.idToken,
        },
      })
      .then((result: AxiosResponse) => {
        this.saveFile(result, fileName);
        return;
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          if (error.response.data.HttpStatusCode === 404 || error.response.status === 404) {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(Intl.Get(LocIds.ServiceTrustClient.ResourceNotFound), 'error_ComplianceManager')
            // );
          } else {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(this.apiErrorMessage, '_common')
            // );
          }
        }
      });
  }

  downloadEvidenceV2(api: string, fileName: string) {
    const url = this.getWebApiEndpointUrl().concat(api);

    axios
      .request({
        responseType: 'arraybuffer',
        url: url,
        method: 'get',
        headers: {
          Authorization: store.getState().authentication.idToken,
        },
      })
      .then((result: AxiosResponse) => {
        this.saveFile(result, fileName);
        return;
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.data) {
          if (error.response.data.HttpStatusCode === 404 || error.response.status === 404) {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(Intl.Get(LocIds.ServiceTrustClient.ResourceNotFound), 'error_ComplianceManager')
            // );
          } else {
            // TODO: Implement Reducer
            // store.dispatch(
            //     StatusMessageReducer.getErrorOccurredAction(this.apiErrorMessage, '_common')
            // );
          }
        }
      });
  }

  saveFile(response: AxiosResponse, fileName: string) {
    // TODO: Need to save file, not needed for now
    // var blob = new Blob([response.data], { type: 'application/octet-stream;charset=utf-8' });
    // FileSaver.saveAs(blob, fileName);
  }
}

export const UATClient = new UATWebApi();
